import React from 'react';
import { Box } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import { DetailsSection } from './sections/DetailsSection.component';
import { CPTCodesSection } from './sections/CPTCodesSection.component';
import { PsychiatryNoteSection } from './components/PsychiatryNoteSection.component';
import { Button, ServerForm, ServerFormFormik, Markdown, Alert } from '@uplift/ui-v2';
import { useSessionPsychiatryNoteQuery, useSessionQuery } from '@uplift/queries';
import { PsychiatryNote, PsychiatryNoteSectionKey, patchSessionPsychiatryNote, psychiatryNoteFormConfigByKey } from '@uplift/api';
import camelCase from 'lodash/camelCase';
import { CamelCase } from '@uplift/types';
import { AssessmentSection } from './sections/AssessmentSection.component';
import { FloatingActionBar } from './components/FloatingActionBar.component';
import { CurrentMedicationSection } from './sections/MedicationSection.component';
import { AllergiesSection } from './sections/AllergiesSection.component';
import { useQueryClient } from '@tanstack/react-query';
import { FormikHelpers } from 'formik';
import { IdentityVerificationSection } from './sections/IdentityVerificationSection.component';
import { SectionPaper, useSectionPaperStyles } from './components/SectionPaper.component';


export type SubmitNoteEditableSectionProps = {
  title?: string;
  autoTitle?: boolean;
  sessionId: string;
  namespace: PsychiatryNoteSectionKey;
  frontendValidation?: boolean;
};


const SubmitNoteEditableSection: React.VFC<SubmitNoteEditableSectionProps> = ({ title: _title, autoTitle, namespace, sessionId, frontendValidation = false }) => {
  const queryClient = useQueryClient();
  const { data } = useSessionPsychiatryNoteQuery(sessionId);
  const { data: session } = useSessionQuery(sessionId);
  const sessionPsychNoteKey = camelCase(namespace) as CamelCase<PsychiatryNoteSectionKey>;
  const sessionPsychNote = data?.forms?.[sessionPsychNoteKey];
  const isError = !!sessionPsychNote?.errors?.length;
  const [ editing, setEditing ] = React.useState(isError);
  const title = autoTitle ? psychiatryNoteFormConfigByKey[sessionPsychNoteKey].title : _title;

  if (editing && sessionPsychNote) {
    const onSubmit = async (values: Record<string, unknown>, helpers: FormikHelpers<Record<string, unknown>>) => {
      const response = await patchSessionPsychiatryNote(sessionId, { forms: { [namespace]: values } });
      const responseData = response.data?.forms[sessionPsychNoteKey];

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [ 'session', sessionId ] }),
        queryClient.invalidateQueries({ queryKey: [ 'appointment', session?.appointmentId, 'session' ] }),
      ]);

      if (responseData?.errors.length) {
        const errorsObj = responseData?.errors.reduce((prev, curr) => ({ ...prev, [curr.id]: curr.message }), {} as Record<string, string>) ?? {};

        helpers.setErrors(errorsObj);

        return;
      }

      setEditing(false);
    };

    return (
      <ServerFormFormik
        formName={namespace}
        errors={sessionPsychNote?.errors ?? []}
        fields={sessionPsychNote?.fields ?? []}
        onSubmit={onSubmit}
        frontendValidation={frontendValidation}
      >
        {formik => {
          const hasError = isError || !formik.isValid;

          return (
            <SectionPaper id={sessionPsychNoteKey} isError={hasError}>
              <PsychiatryNoteSection
                title={title}
                isError={hasError}
                actions={(
                  <Box display="flex" gap="8px">
                    <Button disabled={formik.isSubmitting} onClick={() => setEditing(false)} size="small">Cancel</Button>
                  </Box>
                )}
              >
                <ServerForm fields={sessionPsychNote?.fields ?? []} />
              </PsychiatryNoteSection>
              <FloatingActionBar
                onCancel={() => setEditing(false)}
                onDraftSubmit={frontendValidation ? onSubmit : undefined}
              />
            </SectionPaper>
          );
        }}
      </ServerFormFormik>
    );
  }

  return (
    <SectionPaper id={sessionPsychNoteKey} isError={isError}>
      <PsychiatryNoteSection
        title={title}
        isError={isError}
        actions={<Button onClick={() => setEditing(true)} size="small" color="secondary">Edit</Button>}
      >
        <Box px={3}>
          {sessionPsychNote?.errors?.length ? (
            <Alert title="Missing Information" severity="error">
              Fill out any required information before submitting the session.
            </Alert>
          ) : null}
          <Markdown>{sessionPsychNote?.note ?? ''}</Markdown>
        </Box>
      </PsychiatryNoteSection>
    </SectionPaper>
  );
};


export type PsychiatryNotesSubmitSectionsProps = {
  appointmentId: string;
  sessionId: string;
  patientId: string;
  providerId: string;
};


export const PsychiatryNoteFormsErrorsAlert: React.VFC<{ forms: PsychiatryNote['forms'] }> = ({ forms }) => {
  const errors = React.useMemo(() => {
    const keys = Object.keys(forms) as (keyof typeof forms)[];
    const errorKeys = keys.filter(k => !!forms[k].errors?.length);

    errorKeys.sort((a, b) => psychiatryNoteFormConfigByKey[a].order > psychiatryNoteFormConfigByKey[b].order ? 1 : -1);

    return errorKeys;
  }, [ forms ]);

  if (!errors.length) {
    return null;
  }

  return (
    <Alert severity="error" title="This note needs more information">
      You can submit after you've filled out:
      <ul style={{ paddingLeft: 24 }}>
        {errors.map(key => (
          <li
            key={key}
            style={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => {
              const element = document.getElementById(key);

              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
          >
            {psychiatryNoteFormConfigByKey[key].title}
          </li>
        )
        )}
      </ul>
    </Alert>
  );
};


export const PsychiatryNotesSubmitSections: React.FC<PsychiatryNotesSubmitSectionsProps> = ({ appointmentId, sessionId, patientId }) => {
  const { classes } = useSectionPaperStyles();
  const { data } = useSessionPsychiatryNoteQuery(sessionId);

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <IdentityVerificationSection sessionId={sessionId} appointmentId={appointmentId} />
      <SectionPaper>
        <DetailsSection sessionId={sessionId} appointmentId={appointmentId} />
      </SectionPaper>
      <SubmitNoteEditableSection sessionId={sessionId} namespace="history_of_presenting_illness" autoTitle frontendValidation={true} />
      <SubmitNoteEditableSection sessionId={sessionId} namespace="psychiatric_review_of_system" autoTitle frontendValidation={true} />
      {data?.isInitialSession && (
        <ErrorBoundary fallbackRender={() => null}>
          <React.Suspense fallback={null}>
            <SectionPaper>
              <AllergiesSection patientId={patientId} />
            </SectionPaper>
          </React.Suspense>
        </ErrorBoundary>
      )}
      {data?.isInitialSession && (
        <ErrorBoundary fallbackRender={() => null}>
          <React.Suspense fallback={null}>
            <SectionPaper>
              <CurrentMedicationSection patientId={patientId} />
            </SectionPaper>
          </React.Suspense>
        </ErrorBoundary>
      )}
      {data?.isInitialSession && (
        <SubmitNoteEditableSection sessionId={sessionId} namespace="psychiatric_history" autoTitle frontendValidation={true} />
      )}
      {data?.isInitialSession && (
        <SubmitNoteEditableSection sessionId={sessionId} namespace="social_history" autoTitle frontendValidation={true} />
      )}
      {data?.isInitialSession && (
        <SubmitNoteEditableSection sessionId={sessionId} namespace="medical_history" autoTitle frontendValidation={true} />
      )}
      <SubmitNoteEditableSection sessionId={sessionId} namespace="mental_status_exam" title="MSE" frontendValidation={true} />
      <SubmitNoteEditableSection sessionId={sessionId} namespace="risk_assessment" autoTitle frontendValidation={true} />
      <SubmitNoteEditableSection sessionId={sessionId} namespace="physical_review_of_systems" autoTitle frontendValidation={true} />

      <SectionPaper>
        <AssessmentSection sessionId={sessionId} />
      </SectionPaper>

      <SubmitNoteEditableSection sessionId={sessionId} namespace="diagnosis" autoTitle frontendValidation={true} />
      <SubmitNoteEditableSection sessionId={sessionId} namespace="plan" autoTitle frontendValidation={true} />
      <SubmitNoteEditableSection sessionId={sessionId} namespace="psychotherapy_evaluation_crisis_services" autoTitle frontendValidation={true} />
      <SubmitNoteEditableSection sessionId={sessionId} namespace="treatment_education" autoTitle frontendValidation={true} />
      <SubmitNoteEditableSection sessionId={sessionId} namespace="prescribe_refill" autoTitle frontendValidation={true} />
      <SubmitNoteEditableSection sessionId={sessionId} namespace="additional_information" autoTitle frontendValidation={true} />
      <CPTCodesSection sessionId={sessionId} paperClassName={classes.paper} />
    </Box>
  );
};
